import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { KpiApi, AssetKpi, AssetKpiBatchResponse } from 'src/api';
import { MachineTaskService } from './machine-task.service';
import { MachineInfo, AssetService, ToolingInfo } from '@schenck/rotec-components';
import { switchMap, map, shareReplay, startWith, mapTo, catchError } from 'rxjs/operators';
import { ToolingTaskService } from './tooling-task.service';
import { TaskBaseService } from './task-base.service';

export interface MachineInfoWithKpi extends MachineInfo {
  kpi: AssetKpi;
}

export interface ToolingInfoWithKpi extends ToolingInfo {
  kpi: AssetKpi;
}

const filterOutUndefinedEntries = <T>(assets: T[]): T[] => {
  return assets.filter((asset) => asset !== undefined);
};

@Injectable()
export class KpiService {
  private _kpiServiceError$ = new BehaviorSubject<boolean | string>(false);

  public get kpiServiceError$(): Observable<boolean | string> {
    return this._kpiServiceError$.asObservable();
  }

  machinesWithTasks$: Observable<MachineInfoWithKpi[]> = this.assetService.machines$.pipe(
    map((machines) => machines.filter((machine) => !machine.isDeactivated)),
    switchMap(taskChanges$(this.machineTaskService)),
    switchMap(getKpis<MachineInfoWithKpi>((ids) => this.kpiApi.getMachineKpisBatch$(ids))),
    catchError((error) => {
      this._kpiServiceError$.next(error.error?.message || error?.message || true);
      throw error;
    }),
    map(filterOutUndefinedEntries),
    shareReplay(1)
  );

  toolingsWithTasks$ = this.assetService.toolings$.pipe(
    map((toolings) => toolings.filter((tooling) => !tooling.isDeactivated)),
    switchMap(taskChanges$(this.toolingTaskService)),
    switchMap(getKpis<ToolingInfoWithKpi>((ids) => this.kpiApi.getToolingKpisBatch$(ids))),
    catchError((error) => {
      this._kpiServiceError$.next(error.error?.message || error?.message || true);
      throw error;
    }),
    map(filterOutUndefinedEntries),
    shareReplay(1)
  );

  constructor(
    private kpiApi: KpiApi,
    private machineTaskService: MachineTaskService,
    private toolingTaskService: ToolingTaskService,
    private assetService: AssetService
  ) {}

  getMachineKpis$(id: string): Observable<AssetKpi> {
    return this.machinesWithTasks$.pipe(
      map((machines) => machines.find((m) => m.id === id)),
      map((machine) => machine && machine.kpi)
    );
  }

  getToolingKpis$(id: string): Observable<AssetKpi> {
    return this.toolingsWithTasks$.pipe(
      map((toolings) =>
        toolings.filter((tooling) => !tooling.isDeactivated).find((m) => m.id === id)
      ),
      map((tooling) => tooling && tooling.kpi)
    );
  }
}

function getKpis<T extends { id: string }>(
  getKpis$: (ids: string[]) => Observable<AssetKpiBatchResponse>
): (assets: T[]) => Observable<(T & { kpi: AssetKpi })[]> {
  return (assets: T[]) => {
    if (assets.length > 0) {
      return getKpis$(assets.map((asset) => asset.id)).pipe(
        map((response: AssetKpiBatchResponse) =>
          Object.entries(response).map(([assetId, kpi]) => {
            const asset = assets.find(({ id }) => assetId === id);

            return kpi.numberActiveSerialTasks + kpi.numberActiveTasks !== 0
              ? <T & { kpi: AssetKpi }>{ ...asset, kpi }
              : undefined;
          })
        )
      );
    } else {
      return of<(T & { kpi: AssetKpi })[]>([]);
    }
  };
}

function taskChanges$<T>(service: TaskBaseService<any>) {
  return (assets: T) => service.taskChange$.pipe(startWith(<void>null), mapTo(assets));
}
